// Centralized fetch helper
//
// Purpose:
// - Centralize HTTP requests, JSON body handling, Authorization header injection, and common error handling.
// - Keep API calls consistent and avoid duplicated auth logic across the app.

import { ensureReauth, getAccessTokenSilent } from '../auth/msal';

export interface HttpOptions extends RequestInit { json?: any; auth?: boolean; }

async function getAccessToken(): Promise<string | null> {
  try { return await getAccessTokenSilent(); } catch { return null; }
}

async function triggerReauth() {
  try { await ensureReauth(false); } catch { /* ignore */ }
}

export async function http<T = any>(url: string, options: HttpOptions = {}): Promise<T> {
  const { json, headers, auth = true, ...rest } = options;
  const init: RequestInit = {
    ...rest,
    headers: { 'Content-Type': 'application/json', ...(headers || {}) }
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  if (auth) {
    let token = await getAccessToken();
    if (!token) {
      // Attempt to re-authenticate and do NOT send the request without a token
      await triggerReauth();
      // Throw an auth error to stop current flow; app will redirect and reload
      throw new Error('401 Unauthorized');
    }
    (init.headers as any)['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, init);

  if (auth && res.status === 401) {
    // Token likely expired or invalid; kick off re-auth and surface error
    await triggerReauth();
    throw new Error('401 Unauthorized');
  }

  //if (res.status === 403) throw new Error('Not authorized. Ask an administrator to add your email address.');
  if (!res.ok) {
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data && typeof data === 'object' && 'message' in data) {
        errorMessage = data.message;
      }
    } catch {
      try {
        const text = await res.text();
        if (text) errorMessage = text;
      } catch {}
    }
    throw new Error(errorMessage);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return await res.json();
  return await (res.text() as any);
}
